<template>
  <div class="editableLabel">
    <div
      class="editableLabel-label"
      v-if="!editMode"
      @click="onClickLabel"
    >
      {{ vlabel }}
    </div>
    <div class="d-flex align-items-center">
      <input-text-component
        v-if="editMode"
        v-model="inputValue"
        :maxlength="vmaxlength"
        @onBlur="onBlurInputText"
        :refInput="'editableLabel'"
        :placeholder="vplaceholder"
        :classCustom="{'editableLabel-input noAcceptDrag': true, invalid: isInvalid}"
        @keyup.enter="onEnterInputText"
        id="editable-label-input-text"
        @onInput="onInput"
        :autofocus="true"
      />
      <span class="character-counting" v-if="editMode && isVisibleCharactersRemaining">{{
        charactersRemaining
      }}</span>
    </div>
  </div>
</template>
<script>
import CommonUtils from '../../mixins/commonUtils'
import InputTextComponent from './InputTextComponent.vue'
export default {
  data() {
    return {
      editMode: false, // define whether it is in edit mode or not
      inputValue: '', // v-bind data model for input text
      empty: '', // default placeholder
      updatingValue: false,
      clicks: 0,
      timer: null
    }
  },
  components: { InputTextComponent },
  mixins: [CommonUtils],
  props: {
    text: {
      type: String
    },
    placeholder: {
      type: String
    },
    maxlength: {
      type: Number
    },
    isVisibleCharactersRemaining: {
      type: Boolean
    },
    isRequired: {
      type: Boolean,
      default: true
    },
    disableChangeColumnName: Boolean
  }, // parent should provide :text, :placeholder, :maxlength
  methods: {
    initText() {
      this.updatingValue = false
      this.inputValue = this.text
    },
    /**
     * Switch to Edit mode.
     *
     * Trigger when the div label got clicked
     */
    switchToEditMode() {
      this.editMode = true
      this.$emit('edit-mode', this.editMode)
      this.inputValue = this.text
    },
    /**
     * Update text value.
     *
     * Trigger when the textbox got lost focus or user presses Enter key
     */
    updateText() {
      if (!this.editMode) {
        return
      }
      // validate input text
      const error = this.validate()
      if (error && this.isRequired) {
        this.setCustomValidityInputText('元の名称に戻しました。')
        this.inputValue = this.text
        return
      }
      // update the edit mode to false (to display div label text)
      this.editMode = false
      this.updatingValue = true
      this.$emit('edit-mode', this.editMode)
      // emit text-updated event
      this.$emit('text-updated', this.inputValue)
    },
    /**
     * Update text value.
     *
     * Trigger when the textbox got lost focus
     */
    onBlurInputText() {
      this.updateText()
    },
    /**
     * Update text value.
     *
     * Trigger when user presses Enter key
     */
    onEnterInputText() {
      this.updateText()
    },
    /**
     * Validate input value and return error if any.
     */
    validate() {
      this.inputValue = this.removeEmoji(this.inputValue)
      if (!this.inputValue || this.inputValue.trim() === '') {
        return 'required'
      }

      return null
    },
    isInvalid() {
      return this.validate()
    },
    onClickLabel(e) {
      this.clicks++
      if (this.clicks === 1) {
        this.timer = setTimeout(() => {
          this.$emit('single-click', e)
          this.clicks = 0
        }, 225)
      } else if (!this.disableChangeColumnName) {
        clearTimeout(this.timer)
        this.switchToEditMode()
        this.clicks = 0
      }
    },
    onInput() {
      if (!this.inputValue && this.isRequired) {
        // Display warning message when value is empty
        this.setCustomValidityInputText('空白にはできません。')
      }
    },
    getElementInputText() {
      // get element dom input text
      return document.getElementById('editable-label-input-text')
    },
    // add warning message for input text
    setCustomValidityInputText(message) {
      const input = this.getElementInputText()
      if (input) {
        input.setCustomValidity(message)
        input.reportValidity()
      }
    }
  },
  computed: {
    vmaxlength() {
      return this.maxlength
    },
    vplaceholder() {
      // check if the placeholder is undefined or empty
      if (this.placeholder === undefined || this.placeholder === '') {
        // if it is empty or undefined, pre-populate with built-in placeholder text
        return this.empty
      }
      return this.placeholder
    },
    vlabel() {
      return this.inputValue
    },
    charactersRemaining() {
      if (!this.inputValue) {
        return `0/20`
      }
      return `${this.inputValue.length}/20`
    }
  },
  mounted() {
    // initiate the label view
    this.initText()
  },
  updated() {
    // Make sure EditableLabel is always visible when the input value is empty
    // and user presses Ctrl-Z (after switching to label mode).
    // Make sure label cannot be undo in this case: input a long text -> enter -> switch to edit mode again
    // -> delete the previous input value -> enter -> press ctrl-Z many times.
    if (
      !this.editMode &&
      (!this.inputValue || this.inputValue.trim() === '' ||
        (!this.updatingValue && this.text.trim() !== this.inputValue.trim()))
    ) {
      this.inputValue = this.text
    }
    // focus editablelabel
    var ed = this.$refs.editableLabel
    if (ed != null) {
      ed.focus()
    }
  },
  watch: {
    text(value) {
      this.updatingValue = false
      this.inputValue = value
    }
  }
}
</script>

<style lang="scss" scoped>
  /deep/ .editableLabel-input[type="text"] {
    height: 24px;
    width: 133px;
    padding: 1px 2px;
    border: 1px solid;
    &:focus {
      border-color: #005FCC;
      box-shadow: 0px 0px 0px 0.5px #005FCC;
      transform: scaleY(1);
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/custom-components/EditableLabel.vue